import { useEffect, useState } from 'react';
import api, { SERVER_BASE } from '../api';
import Modal from './Modal';

const DOC_TITLE_LV = {
  invoice:       'RĒĶINS',
  advance:       'AVANSA RĒĶINS',
  estimate:      'TĀME',
  delivery_note: 'PAVADZĪME',
  credit_note:   'KREDĪTRĒĶINS',
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const initialsOf = (name) => (name || 'CO').split(/\s+/)
  .map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

const fmt = (n) => Number(n || 0).toLocaleString('lv-LV', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Negatīvas summas (kredītrēķins) → "− € 700,00", tāpat kā servera PDF
const money = (n) => { const v = Number(n) || 0; return `${v < 0 ? '− ' : ''}€ ${fmt(Math.abs(v))}`; };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('lv-LV') : '—';

const InvoicePreview = ({ invoiceId, onClose, onDownloadPdf }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!invoiceId) return;
    api.get(`/invoices/${invoiceId}/full`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.message || 'Kļūda ielādējot rēķinu'));
  }, [invoiceId]);

  if (err) {
    return (
      <Modal title="Kļūda" onClose={onClose} maxWidth={400}>
        <div style={{ color: '#dc2626' }}>{err}</div>
      </Modal>
    );
  }

  if (!data) {
    return (
      <Modal title="Rēķina priekšskatījums" onClose={onClose} maxWidth={820}>
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Ielādē…</div>
      </Modal>
    );
  }

  const { invoice, company, items, advances } = data;
  const docType = invoice.doc_type || 'invoice';
  const title = DOC_TITLE_LV[docType] || 'RĒĶINS';
  const PRIMARY = HEX_RE.test(company.primary_color || '') ? company.primary_color : '#C41E1E';
  const noVat = !String(company.vat_number || '').trim();
  const daysLabel = invoice.days_label || 'Dienas';
  const hasDays = !!invoice.days_label;

  const logoUrl = company.logo_path ? `${SERVER_BASE}${company.logo_path}` : null;

  const advanceTotal = (advances || []).reduce((s, a) => s + Number(a.amount_applied || 0), 0);
  const grossTotal = Number(invoice.total_incl_vat) + advanceTotal;
  // total_excl_vat DB laukā ir summa PĒC rēķina atlaides → summa pirms atlaides
  // = total_excl_vat + discount_amount (tāda pati loģika kā servera PDF).
  const exclAfterDisc = Number(invoice.total_excl_vat) || 0;
  const discountAmt = Number(invoice.discount_amount) || 0;
  const hasDiscount = discountAmt !== 0; // kredītrēķinam atlaide ir negatīva — arī tad jārāda

  return (
    <Modal title={`${title} ${invoice.invoice_number || '(draft)'}`} onClose={onClose} maxWidth={860}>
      {/* Toolbar — lejupielādēt PDF poga */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 8,
        marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #f1f5f9',
      }}>
        {onDownloadPdf && (
          <button className="btn btn-primary btn-sm" onClick={() => onDownloadPdf(invoice)}>
            📄 Lejupielādēt PDF
          </button>
        )}
      </div>

      {/* A4 lapas simulācija */}
      <div style={{
        background: 'white', color: '#333',
        fontFamily: 'Helvetica, Arial, sans-serif', fontSize: 12, lineHeight: 1.4,
        padding: 36, borderRadius: 6, border: '1px solid #e2e8f0',
      }}>
        {/* ── Header: wide logo + invoice title/meta ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, gap: 16 }}>
          <div style={{ flex: 1, maxWidth: '50%' }}>
            {logoUrl ? (
              <img src={logoUrl} alt="logo"
                style={{ maxWidth: 220, maxHeight: 70, objectFit: 'contain', display: 'block' }} />
            ) : (
              <div style={{
                color: PRIMARY, fontSize: 26, fontWeight: 800, fontStyle: 'italic',
                letterSpacing: -0.5, textTransform: 'uppercase', lineHeight: 1,
              }}>
                {company.name || '—'}
              </div>
            )}
            <div style={{ marginTop: 14, fontSize: 11, color: '#666', lineHeight: 1.5 }}>
              <strong style={{ color: '#333' }}>{company.name || ''}</strong>
              {company.address && <div>{company.address}</div>}
              {company.email && <div>{company.email}</div>}
              {company.phone && <div>{company.phone}</div>}
            </div>
          </div>
          <div style={{ textAlign: 'right', minWidth: 220 }}>
            <div style={{ color: PRIMARY, fontSize: 30, fontWeight: 900, textTransform: 'uppercase', lineHeight: 1, marginBottom: 12 }}>
              {title}
            </div>
            <div style={{ fontSize: 11 }}>
              {[
                [`${title.charAt(0) + title.slice(1).toLowerCase()} Nr:`, invoice.invoice_number],
                ['Datums:', fmtDate(invoice.date)],
                ...(docType === 'credit_note' && (invoice.credited_number || invoice.credited_date)
                  ? [['Pie rēķina Nr:', `${invoice.credited_number || ''}${invoice.credited_date ? ` (${fmtDate(invoice.credited_date)})` : ''}`]]
                  : []),
                ...(invoice.due_date && docType !== 'estimate' && docType !== 'credit_note'
                  ? [['Apmaksas termiņš:', fmtDate(invoice.due_date)]] : []),
              ].map(([lbl, val], i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 4 }}>
                  <span style={{ color: PRIMARY, fontWeight: 700 }}>{lbl}</span>
                  <span style={{ minWidth: 100, textAlign: 'right' }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Klients ── */}
        <div style={{ marginBottom: 24 }}>
          <span style={{
            display: 'inline-block', background: PRIMARY, color: 'white',
            padding: '5px 14px', fontSize: 10, fontWeight: 700, letterSpacing: 1,
            textTransform: 'uppercase', marginBottom: 8,
          }}>
            {docType === 'delivery_note' ? 'Saņēmējs' : 'Klients'}
          </span>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 2 }}>
            {invoice.client_name || '—'}
          </div>
          <div style={{ color: '#444', fontSize: 11, lineHeight: 1.6 }}>
            {invoice.client_address && <div>{invoice.client_address}</div>}
            {invoice.client_reg && <div>Reģ. Nr: {invoice.client_reg}</div>}
            {invoice.client_vat && <div>PVN Nr: {invoice.client_vat}</div>}
            {invoice.client_email && <div>{invoice.client_email}</div>}
          </div>
        </div>

        {/* ── Items ── */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 18 }}>
          <thead>
            <tr style={{
              background: '#f4f4f4', color: PRIMARY,
              borderBottom: `2px solid ${PRIMARY}`,
            }}>
              <th style={{ padding: '10px 6px', textAlign: 'left', width: 26, textTransform: 'uppercase', fontSize: 10 }}>Nr.</th>
              <th style={{ padding: '10px 6px', textAlign: 'left', textTransform: 'uppercase', fontSize: 10 }}>Apraksts</th>
              <th style={{ padding: '10px 6px', textAlign: 'right', width: 48, textTransform: 'uppercase', fontSize: 10 }}>Daudz.</th>
              {hasDays && <th style={{ padding: '10px 6px', textAlign: 'right', width: 48, textTransform: 'uppercase', fontSize: 10 }}>{daysLabel}</th>}
              <th style={{ padding: '10px 6px', textAlign: 'right', width: 70, textTransform: 'uppercase', fontSize: 10 }}>Cena €</th>
              <th style={{ padding: '10px 6px', textAlign: 'right', width: 50, textTransform: 'uppercase', fontSize: 10 }}>Atl. %</th>
              {!noVat && <th style={{ padding: '10px 6px', textAlign: 'right', width: 50, textTransform: 'uppercase', fontSize: 10 }}>PVN %</th>}
              <th style={{ padding: '10px 6px', textAlign: 'right', width: 80, textTransform: 'uppercase', fontSize: 10 }}>Summa €</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((item, i) => (
              <tr key={item.id || i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px 6px' }}>{i + 1}</td>
                <td style={{ padding: '10px 6px' }}>{item.description || ''}</td>
                <td style={{ padding: '10px 6px', textAlign: 'right' }}>{item.quantity}</td>
                {hasDays && (
                  <td style={{ padding: '10px 6px', textAlign: 'right' }}>
                    {Number(item.days) > 0 ? item.days : '—'}
                  </td>
                )}
                <td style={{ padding: '10px 6px', textAlign: 'right' }}>{Number(item.unit_price).toFixed(2)}</td>
                <td style={{ padding: '10px 6px', textAlign: 'right' }}>{Number(item.discount_pct || 0).toFixed(0)}%</td>
                {!noVat && <td style={{ padding: '10px 6px', textAlign: 'right' }}>{item.vat_rate ?? 21}%</td>}
                <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700 }}>{Number(item.total).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Totals — grand total in red bar ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 18 }}>
          <div style={{ minWidth: 280, fontSize: 12 }}>
            {hasDiscount && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px' }}>
                  <span style={{ color: '#555' }}>Summa bez atlaides:</span>
                  <span>{money(exclAfterDisc + discountAmt)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', color: PRIMARY }}>
                  <span>Atlaide{Number(invoice.discount_pct) > 0 ? ` (${Number(invoice.discount_pct).toFixed(1)}%)` : ''}:</span>
                  <span>{money(-discountAmt)}</span>
                </div>
              </>
            )}
            {!noVat && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px' }}>
                  <span style={{ color: '#555' }}>Summa bez PVN:</span>
                  <span>{money(exclAfterDisc)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px' }}>
                  <span style={{ color: '#555' }}>PVN:</span>
                  <span>{money(invoice.vat_amount)}</span>
                </div>
              </>
            )}
            {advances && advances.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px' }}>
                  <span style={{ color: '#555' }}>Kopā:</span>
                  <span>€ {fmt(grossTotal)}</span>
                </div>
                {advances.map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', color: PRIMARY, fontSize: 11 }}>
                    <span>Avansa rēķ. {a.advance_number}{a.advance_date ? ` (${fmtDate(a.advance_date)})` : ''}:</span>
                    <span>− € {fmt(a.amount_applied)}</span>
                  </div>
                ))}
              </>
            )}
            <div style={{
              display: 'flex', justifyContent: 'space-between', padding: '10px 14px',
              background: PRIMARY, color: 'white', fontWeight: 700, fontSize: 14, marginTop: 8,
            }}>
              <span>{docType === 'delivery_note' ? 'KOPĀ:' : docType === 'credit_note' ? 'KOPĀ KREDITĒTS:' : 'KOPĀ APMAKSĀT:'}</span>
              <span>{money(invoice.total_incl_vat)}</span>
            </div>
          </div>
        </div>

        {/* ── Footer: Apmaksas rekvizīti + small square logo ── */}
        {docType !== 'delivery_note' && (company.name || company.bank_name || company.iban) && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
            gap: 20, marginTop: 30, paddingTop: 16, borderTop: '1px solid #eee',
          }}>
            <div style={{ flex: 1, fontSize: 11, color: '#333', lineHeight: 1.7 }}>
              <div style={{ color: PRIMARY, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5 }}>
                Apmaksas rekvizīti
              </div>
              {company.name       && <div><strong>Saņēmējs:</strong> {company.name}</div>}
              {company.reg_number && <div><strong>Reģ. Nr.:</strong> {company.reg_number}</div>}
              {company.vat_number && <div><strong>PVN Nr.:</strong> {company.vat_number}</div>}
              {company.bank_name  && <div><strong>Banka:</strong> {company.bank_name}</div>}
              {company.iban       && <div><strong>IBAN:</strong> {company.iban}</div>}
              <div><strong>Maksājuma identifikators:</strong> {invoice.invoice_number}</div>
              <div style={{ marginTop: 10, fontStyle: 'italic', color: '#888' }}>Paldies par sadarbību!</div>
            </div>
            <div style={{
              width: 50, height: 50, background: logoUrl ? 'white' : PRIMARY,
              border: logoUrl ? `1px solid ${PRIMARY}` : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 4, color: 'white', fontWeight: 700, fontSize: 18,
              flexShrink: 0, overflow: 'hidden',
            }}>
              {logoUrl
                ? <img src={logoUrl} alt="" style={{ maxWidth: 44, maxHeight: 44, objectFit: 'contain' }} />
                : initialsOf(company.name)}
            </div>
          </div>
        )}

        {/* ── Piezīmes ── */}
        {invoice.notes && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Piezīmes:</div>
            <div style={{ fontSize: 11, color: '#475569', whiteSpace: 'pre-wrap' }}>{invoice.notes}</div>
          </div>
        )}

        {/* ── Paraksti / footer ── */}
        {invoice.printed_version ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 30, marginTop: 30, paddingTop: 20 }}>
            <div style={{ flex: 1, borderTop: '1px solid #94a3b8', paddingTop: 4, fontSize: 10, color: '#64748b' }}>
              Izrakstīja (paraksts, atšifrējums)
            </div>
            <div style={{ flex: 1, borderTop: '1px solid #94a3b8', paddingTop: 4, fontSize: 10, color: '#64748b' }}>
              Saņēmējs (paraksts, atšifrējums)
            </div>
          </div>
        ) : invoice.footer_electronic && (
          <div style={{
            color: '#94a3b8', fontSize: 10, fontStyle: 'italic', textAlign: 'center',
            marginTop: 24, paddingTop: 12, borderTop: '1px solid #f1f5f9',
          }}>
            Rēķins sagatavots elektroniski un derīgs bez paraksta.
          </div>
        )}
      </div>

      {/* Metadati (darbinieks, statuss) */}
      <div style={{
        marginTop: 12, fontSize: 12, color: '#64748b',
        display: 'flex', gap: 20, flexWrap: 'wrap',
      }}>
        <div>Statuss: <strong>{invoice.status}</strong></div>
        {invoice.employee_name && <div>Darbinieks: <strong>{invoice.employee_name}</strong></div>}
        {invoice.sent_at && <div>Nosūtīts: {new Date(invoice.sent_at).toLocaleString('lv-LV')}</div>}
      </div>
    </Modal>
  );
};

export default InvoicePreview;
